import { cn } from "@/lib/utils";
import { CertificationTypes } from "@/types/main-types";
import { motion } from "framer-motion";
import { BadgeCheck, MoveUpRight } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

/* ─── tiny hook: detect element in viewport ─── */

export function useInView(ref: React.RefObject<HTMLElement>) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.15 },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

/* ─── single cert card ─── */
export function CertCard({
  cert,
  idx,
  isAr,
  onOpen,
}: {
  cert: CertificationTypes;
  idx: number;
  isAr: boolean;
  onOpen: (img: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null!);
  const visible = useInView(ref);
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.55,
        delay: (idx % 3) * 0.08,
        ease: [0.22, 1, 0.36, 1],
      }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={() => onOpen(cert.image)}
      className={cn(
        "group relative flex flex-col rounded-[28px] overflow-hidden cursor-pointer transition-shadow duration-300",
        "bg-white border-[1.5px]",
        hovered
          ? "border-[#1A1A2E] shadow-[0_24px_60px_-12px_rgba(26,26,46,0.14)]"
          : "border-[#E8E8EE] shadow-[0_2px_12px_0_rgba(26,26,46,0.04)]",
      )}
    >
      {/* Image panel */}
      <div
        className={cn(
          "relative h-[230px] bg-[#F7F7FB] border-b-[1.5px] overflow-hidden shrink-0 transition-colors duration-250",
          hovered ? "border-[#1A1A2E]" : "border-[#E8E8EE]",
        )}
      >
        <Image
          src={cert.image}
          alt={isAr ? cert.nameAr : cert.nameEn}
          fill
          className="object-contain p-8"
          style={{
            transform: hovered ? "scale(1.06)" : "scale(1)",
            transition: "transform 0.55s cubic-bezier(0.22,1,0.36,1)",
          }}
        />

        {/* hover reveal overlay */}
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center",
            "bg-[rgba(26,26,46,0.55)] backdrop-blur-[6px]",
            "transition-opacity duration-300",
            hovered ? "opacity-100" : "opacity-0",
          )}
        >
          <div
            className="flex items-center gap-2 bg-white rounded-full px-[22px] py-2.5 text-[13px] font-semibold text-[#1A1A2E] tracking-[0.01em]"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {isAr ? "عرض الشهادة" : "View Certificate"}
            <MoveUpRight size={14} />
          </div>
        </div>
      </div>

      {/* Body */}
      <div
        className={cn(
          "flex flex-col flex-1 px-7 pt-6 pb-7",
          isAr ? "items-end text-right" : "items-start text-left",
        )}
      >
        {/* Verified badge */}
        <div
          className="inline-flex items-center gap-[5px] bg-[#EEF9F4] text-[#1A8F5A] rounded-full px-2.5 py-1 text-[11px] font-medium tracking-[0.07em] mb-3.5"
          style={{ fontFamily: "'DM Mono', monospace" }}
        >
          <BadgeCheck size={12} />
          {isAr ? "معتمد" : "VERIFIED"}
        </div>

        <h3
          className="text-[clamp(1.05rem,1.8vw,1.35rem)] font-bold text-[#1A1A2E] leading-tight tracking-[-0.025em] mb-2.5"
          style={{ fontFamily: "'Syne', sans-serif" }}
        >
          {isAr ? cert.nameAr : cert.nameEn}
        </h3>

        <p
          className="line-clamp-2 text-sm text-[#7A7A90] leading-[1.65] mb-[22px]"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          {isAr ? cert.descriptionAr : cert.descriptionEn}
        </p>

        {/* Bottom CTA row */}
        <div
          className={cn(
            "mt-auto flex items-center gap-3",
            isAr ? "flex-row-reverse" : "flex-row",
          )}
        >
          <div
            className={cn(
              "w-9 h-9 rounded-full flex items-center justify-center transition-colors duration-250",
              hovered ? "bg-[#1A1A2E]" : "bg-[#F3F3F7]",
            )}
          >
            <MoveUpRight
              size={15}
              className={cn(
                "transition-colors duration-250",
                isAr ? "-scale-x-100" : "",
              )}
              color={hovered ? "#FFFFFF" : "#8888A0"}
            />
          </div>
          <span
            className={cn(
              "text-[11px] font-medium tracking-widest uppercase transition-colors duration-250",
              hovered ? "text-[#1A1A2E]" : "text-[#AAAABC]",
            )}
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            {isAr ? "مشاهدة الشهادة" : "View Certificate"}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
